<script setup>
import { ref, computed } from 'vue';

// Props für die Komponente
const props = defineProps({
  showId: {
    type: [Number, String],
    required: true
  },
  venue: {
    type: String,
    required: true
  },
  date: {
    type: String,
    required: true
  },
  location: {
    type: String,
    default: ''
  },
  time: {
    type: String,
    default: ''
  }
});

// State für das Popup
const isOpen = ref(false);
const calendarLinks = ref(null);
const isLoading = ref(false);
const error = ref(null);

// Formatierte Datum-Anzeige
const formattedDate = computed(() => {
  const date = new Date(props.date);
  let formatted = date.toLocaleDateString('de-DE', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
  
  if (props.time) {
    formatted += `, ${props.time} Uhr`;
  }
  
  return formatted;
});

// Popup öffnen und Kalender-Links laden
const openCalendarPopup = async () => {
  isOpen.value = true;
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`/api/calendar_export.php?id=${props.showId}`);
    const data = await response.json();
    
    if (data.success && data.calendar_links) {
      calendarLinks.value = data.calendar_links;
    } else {
      throw new Error(data.error || 'Fehler beim Laden der Kalender-Links');
    }
  } catch (err) {
    console.error('Fehler beim Laden der Kalender-Links:', err);
    error.value = 'Die Kalender-Links konnten nicht geladen werden. Bitte versuchen Sie es später erneut.';
  } finally {
    isLoading.value = false;
  }
};

// Popup schließen
const closeCalendarPopup = () => {
  isOpen.value = false;
};

// Vermeiden, dass Klicks auf das Popup es schließen
const handlePopupClick = (e) => {
  e.stopPropagation();
};

// Link zu einem Kalender öffnen
const openCalendarLink = (url) => {
  window.open(url, '_blank');
  closeCalendarPopup();
};
</script>

<template>
  <div class="calendar-buttons">
    <button 
      class="calendar-btn" 
      title="Zu Kalender hinzufügen"
      @click="openCalendarPopup"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
      <span>Zu Kalender hinzufügen</span>
    </button>
    
    <!-- Backdrop für das Popup -->
    <div 
      v-if="isOpen" 
      class="calendar-popup-backdrop"
      @click="closeCalendarPopup"
    >
      <!-- Popup-Inhalt -->
      <div class="calendar-popup" @click="handlePopupClick">
        <button class="close-btn" @click="closeCalendarPopup">×</button>
        
        <h3>Zu Kalender hinzufügen</h3>
        <p class="event-info">
          <strong>{{ venue }}</strong><br>
          {{ formattedDate }}
          <span v-if="location">, {{ location }}</span>
        </p>
        
        <div v-if="isLoading" class="loading">
          <span class="loading-spinner"></span>
          Kalender-Links werden geladen...
        </div>
        
        <div v-else-if="error" class="error">
          {{ error }}
        </div>
        
        <div v-else-if="calendarLinks" class="calendar-options">
          <!-- Google Kalender -->
          <button 
            class="calendar-option google"
            @click="openCalendarLink(calendarLinks.google)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#4285F4">
              <path d="M21.56 10.25l-9.56-8.5c-.27-.25-.69-.25-.96 0l-9.56 8.5c-.25.22-.28.59-.06.84l.34.4c.22.25.59.28.84.06l1.4-1.24v10.69c0 .33.26.6.59.6h13.8c.33 0 .59-.27.59-.6V10.31l1.4 1.24c.25.22.62.19.84-.06l.34-.4c.22-.25.19-.62-.06-.84zM11 17.75c0 .55-.45 1-1 1h-2c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h2c.55 0 1 .45 1 1v4zm6 0c0 .55-.45 1-1 1h-2c-.55 0-1-.45-1-1v-7c0-.55.45-1 1-1h2c.55 0 1 .45 1 1v7z"/>
            </svg>
            Google Kalender
          </button>
          
          <!-- Outlook -->
          <button 
            class="calendar-option outlook"
            @click="openCalendarLink(calendarLinks.outlook)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#0072C6">
              <path d="M21.2,5.3H9.8c-0.4,0-0.8,0.3-0.8,0.8v4.6l6.1,1.8l6.8-1.8V6.1C21.9,5.7,21.6,5.3,21.2,5.3z"/>
              <path d="M9,12.5V17c0,0.4,0.3,0.8,0.8,0.8h11.5c0.4,0,0.8-0.3,0.8-0.8v-4.6l-6.8,1.9L9,12.5z"/>
              <path d="M8.6,13.1L2.3,11.6v6.5l6.3-2.2V13.1z"/>
              <path d="M2.3,5.9v5.7l6.3,1.5V7.7L2.3,5.9z"/>
            </svg>
            Outlook
          </button>
          
          <!-- Yahoo -->
          <button 
            class="calendar-option yahoo"
            @click="openCalendarLink(calendarLinks.yahoo)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#5F01D1">
              <path d="M12.6,12.3c-0.4,0.7-0.8,1.3-1.2,1.9c-0.4,0.7-0.8,1.3-1.2,1.9c-0.4,0.6-0.7,1.1-1,1.6C9,17.9,8.8,18.2,8.6,18.4 c-0.2,0.3-0.4,0.6-0.5,0.8s-0.2,0.4-0.3,0.5c-0.1,0.1-0.1,0.2-0.2,0.2s-0.1,0-0.1,0s-0.1,0-0.1,0c-0.1,0-0.1,0-0.1-0.1 c0,0,0-0.1,0-0.1c0-0.3,0-0.5,0-0.8c0-0.3,0-0.6,0-0.9c0-0.3,0-0.6,0-0.9c0-0.3,0-0.5,0-0.8c0-1.2,0-2.4,0-3.6 c0-0.6,0-1.2,0.1-1.9c0-0.6,0.1-1.2,0.2-1.8H7.5c-0.3,0-0.6,0-0.9,0c-0.3,0-0.6,0-0.9,0c-0.3,0-0.6,0-0.8,0s-0.4,0-0.6,0 c0,0-0.1,0-0.1,0c0,0,0,0,0-0.1c0,0,0-0.1,0.1-0.1l0,0c0.1,0,0.3-0.1,0.5-0.1c0.2,0,0.5-0.1,0.7-0.1c0.2,0,0.4-0.1,0.6-0.1 c0.1,0,0.2,0,0.3,0c0.3,0,0.6,0,0.9-0.1c0.3,0,0.6-0.1,0.9-0.1c0.3,0,0.6-0.1,0.8-0.1c0.2,0,0.4,0,0.5,0c-0.1,0.3-0.2,0.7-0.3,1 c-0.1,0.3-0.2,0.7-0.3,1c-0.1,0.3-0.2,0.6-0.2,0.9s-0.1,0.5-0.1,0.7h0c0,0,0.1,0,0.1,0c0.3-0.4,0.6-0.9,0.9-1.3 c0.3-0.5,0.6-0.9,0.9-1.3c0.3-0.4,0.6-0.9,0.9-1.3c0.3-0.4,0.6-0.9,0.9-1.3c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.1,0.1-0.2 c0,0,0.1-0.1,0.1-0.1c0,0,0.1,0,0.1,0h0c0.3,0,0.7,0,1,0c0.3,0,0.7,0,1,0c0.3,0,0.7,0,1,0c0.3,0,0.7,0,1,0c0,0,0.1,0,0.1,0 c0,0,0,0,0,0.1c0,0,0,0.1-0.1,0.1l0,0c-0.3,0.1-0.5,0.2-0.8,0.3c-0.2,0.1-0.4,0.2-0.6,0.3C14,8.9,13.8,9,13.7,9.1 c-0.1,0.1-0.3,0.2-0.4,0.3c-0.3,0.3-0.6,0.6-0.9,1c-0.3,0.3-0.6,0.7-0.9,1.1c-0.3,0.4-0.5,0.7-0.8,1.1c-0.2,0.3-0.4,0.7-0.6,1 c0,0,0,0.1-0.1,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0.4,0.5,0.8,1,1.2,1.5c0.4,0.5,0.8,1,1.1,1.5 c0.4,0.5,0.7,1,1.1,1.4c0.3,0.5,0.7,0.9,1,1.3c0.1,0.1,0.2,0.2,0.3,0.3c0.1,0.1,0.2,0.2,0.2,0.3c0.1,0.1,0.1,0.1,0.2,0.2 c0.1,0.1,0.1,0.1,0.2,0.1c0.2,0.2,0.4,0.3,0.6,0.4c0.2,0.1,0.4,0.2,0.6,0.3c0.2,0.1,0.4,0.2,0.6,0.2c0.2,0.1,0.4,0.1,0.6,0.2 c0,0,0.1,0,0.1,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1-0.1,0.1l0,0c-0.3,0-0.6,0-0.8,0c-0.3,0-0.5,0-0.8,0c-0.3,0-0.5,0-0.8,0 c-0.3,0-0.5,0-0.8,0c-0.3,0-0.5,0-0.8,0c-0.3,0-0.5,0-0.8,0c0,0-0.1,0-0.1,0 c0,0,0-0.1,0-0.1c0,0,0-0.1,0.1-0.1l0,0c0.2,0,0.3-0.1,0.5-0.1c0.2,0,0.3-0.1,0.5-0.1c0.2,0,0.3-0.1,0.5-0.1c0.2,0,0.3-0.1,0.5-0.1 c0.1,0,0.2-0.1,0.3-0.1c0.1,0,0.2-0.1,0.2-0.1c0.1,0,0.1-0.1,0.2-0.1c0.1,0,0.1-0.1,0.2-0.1c0-0.1,0-0.2-0.1-0.3 c0-0.1-0.1-0.3-0.2-0.4c-0.1-0.2-0.2-0.3-0.3-0.5c-0.1-0.2-0.2-0.4-0.4-0.6c-0.2-0.4-0.5-0.7-0.7-1.1c-0.2-0.4-0.5-0.7-0.7-1.1 c-0.2-0.4-0.5-0.7-0.7-1.1C13.1,13,12.9,12.6,12.6,12.3z"/>
              <path d="M18.9,19.9c-0.2,0-0.4,0-0.5,0.1c-0.1,0.1-0.3,0.2-0.4,0.3c-0.1,0.1-0.2,0.3-0.2,0.4c-0.1,0.2-0.1,0.3-0.1,0.5 c0,0.2,0,0.3,0.1,0.5c0.1,0.1,0.1,0.3,0.2,0.4c0.1,0.1,0.2,0.2,0.4,0.3c0.2,0.1,0.3,0.1,0.5,0.1c0.2,0,0.4,0,0.5-0.1 c0.1-0.1,0.3-0.2,0.4-0.3c0.1-0.1,0.2-0.3,0.2-0.4c0.1-0.2,0.1-0.3,0.1-0.5c0-0.2,0-0.3-0.1-0.5c-0.1-0.1-0.1-0.3-0.2-0.4 c-0.1-0.1-0.2-0.2-0.4-0.3C19.2,19.9,19.1,19.9,18.9,19.9z"/>
            </svg>
            Yahoo
          </button>
          
          <!-- ICS-Datei Download -->
          <button 
            class="calendar-option ics"
            @click="openCalendarLink(calendarLinks.ics)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#636e72">
              <path d="M19,3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V5C21,3.9,20.1,3,19,3z M17,7h-2v2h2V7z M17,11h-2v2h2V11z
 M17,15h-2v2h2V15z M13,7h-2v2h2V7z M13,11h-2v2h2V11z M13,15h-2v2h2V15z M9,7H7v2h2V7z M9,11H7v2h2V11z M9,15H7v2h2V15z"/>
            </svg>
            ICS-Datei (Apple Calendar, Outlook)
          </button>
        </div>
        
        <div class="calendar-info">
          Durch Hinzufügen dieses Events zu Ihrem Kalender bleiben Sie über den Termin informiert und verpassen kein Konzert von HomeWard.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calendar-buttons {
  position: relative;
}

.calendar-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background-color: var(--white);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  color: var(--primary-color);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-btn:hover {
  background-color: rgba(0, 132, 61, 0.1);
}

.calendar-btn svg {
  color: var(--primary-color);
}

/* Responsive Button */
@media (max-width: 768px) {
  .calendar-btn span {
    display: none;
  }
  
  .calendar-btn {
    padding: 0.4rem;
  }
}

/* Calendar Popup */
.calendar-popup-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.calendar-popup {
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.close-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  color: #666;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.calendar-popup h3 {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--primary-color);
}

.event-info {
  margin-bottom: 1.5rem;
  line-height: 1.4;
}

.calendar-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.calendar-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: white;
  border: 1px solid #eee;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
}

.calendar-option svg {
  margin-bottom: 0.25rem;
}

.calendar-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.calendar-option.google:hover {
  border-color: #4285F4;
}

.calendar-option.outlook:hover {
  border-color: #0072C6;
}

.calendar-option.yahoo:hover {
  border-color: #5F01D1;
}

.calendar-option.ics:hover {
  border-color: #636e72;
}

.calendar-info {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  color: #666;
}

.loading-spinner {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 132, 61, 0.2);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  color: #e74c3c;
  text-align: center;
  padding: 1rem;
}

/* Responsive für mobile Geräte */
@media (max-width: 576px) {
  .calendar-options {
    grid-template-columns: 1fr;
  }
  
  .calendar-option {
    flex-direction: row;
    justify-content: flex-start;
  }
}
</style> 